import React from 'react';
import styled from 'styled-components';
import Navigation from './Navigation';
import { useLanguage } from './LanguageContext';
import { useCookieConsent } from '../CookieConsent';

const Main = styled.main`
  min-height: 100vh;
`;

const Footer = styled.footer`
  background: var(--background-dark);
  padding: 3rem 2rem;
  color: var(--text);
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
`;

const FooterSection = styled.div`
  h3 {
    font-size: 1.2rem;
    margin-bottom: 1rem;
    color: var(--text);
  }

  p, a {
    color: var(--text-secondary);
    margin-bottom: 0.5rem;
    text-decoration: none;
    display: block;
    
    &:hover {
      color: var(--primary);
    }
  }
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: inherit;
  color: var(--text-secondary);
  margin-bottom: 0.5rem;
  text-align: left;
  display: block;

  &:hover {
    color: var(--primary);
  }
`;

const Copyright = styled.div`
  text-align: center;
  padding-top: 2rem;
  margin-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--text-secondary);
`;

interface LayoutProps {
  children: React.ReactNode;
}

const footerContent = {
  de: {
    company: 'IT-Beratung Reinke',
    slogan1: 'Data & Analytics, Prozessautomatisierung & KI-Lösungen',
    slogan2: 'Ich verwandle Ihre Daten in intelligente Lösungen.',
    contact: 'Kontakt',
    address: 'Sonthofen, Deutschland',
    links: 'Links',
    about: 'Über mich',
    services: 'Leistungen',
    tools: 'Demo Projekte',
    contactLink: 'Kontakt',
    legal: 'Legal',
    privacy: 'Datenschutz',
    imprint: 'Impressum',
    terms: 'AGB',
    cookieSettings: 'Cookie-Einstellungen',
    copyright: 'Alle Rechte vorbehalten.'
  },
  en: {
    company: 'IT Consulting Reinke',
    slogan1: 'Data & Analytics, Process Automation & AI Solutions',
    slogan2: 'We transform your data into intelligent solutions.',
    contact: 'Contact',
    address: 'Sonthofen, Germany',
    links: 'Links',
    about: 'About',
    services: 'Services',
    tools: 'Demo Projects',
    contactLink: 'Contact',
    legal: 'Legal',
    privacy: 'Privacy Policy',
    imprint: 'Imprint',
    terms: 'Terms',
    cookieSettings: 'Cookie Settings',
    copyright: 'All rights reserved.'
  }
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { language } = useLanguage();
  const { openSettings } = useCookieConsent();
  const t = footerContent[language];
  
  const scrollToSection = (sectionId: string) => {
    if (window.location.pathname === '/') {
      const section = document.getElementById(sectionId);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      window.location.href = `/#${sectionId}`;
    }
  };

  return (
    <>
      <Navigation />
      <Main>{children}</Main>
      <Footer>
        <FooterContent>
          <FooterSection>
            <h3>{t.company}</h3>
            <p>{t.slogan1}</p>
            <p>{t.slogan2}</p>
          </FooterSection>
          <FooterSection>
            <h3>{t.contact}</h3>
            <a href="mailto:kontakt@it-beratung-reinke.de">kontakt@it-beratung-reinke.de</a>
            <p>{t.address}</p>
          </FooterSection>
          <FooterSection>
            <h3>{t.links}</h3>
            <a href="#about" onClick={(e) => { e.preventDefault(); scrollToSection('about'); }}>{t.about}</a>
            <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }}>{t.services}</a>
            <a href="#projects" onClick={(e) => { e.preventDefault(); scrollToSection('projects'); }}>{t.tools}</a>
            <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}>{t.contactLink}</a>
          </FooterSection>
          <FooterSection>
            <h3>{t.legal}</h3>
            <a href={language === 'de' ? '/privacy-de' : '/privacy-en'}>{t.privacy}</a>
            <a href={language === 'de' ? '/imprint-de' : '/imprint-en'}>{t.imprint}</a>
            <a href="/terms">{t.terms}</a>
            <LinkButton type="button" onClick={openSettings}>{t.cookieSettings}</LinkButton>
          </FooterSection>
        </FooterContent>
        <Copyright>
          © {new Date().getFullYear()} IT-Beratung Reinke. {t.copyright}
        </Copyright>
      </Footer>
    </>
  );
};

export default Layout; 